import { useEffect, useRef, useState, useCallback } from "react";

export const useWebSocket = (url, options = {}) => {
  const {
    onMessage,
    onOpen,
    onClose,
    onError,
    reconnect = true,
    reconnectInterval = 3000,
  } = options;

  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);

  const [readyState, setReadyState] = useState(WebSocket.CLOSED);
  const [connected, setConnected] = useState(false);

  const connect = useCallback(() => {
    if (!url) return;

    // Avoid multiple connect call
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;

    const token = localStorage.getItem("token");
    const fullUrl = token
      ? `${url}${url.includes("?") ? "&" : "?"}token=${token}`
      : url;

    // Close previous socket if any
    if (wsRef.current) wsRef.current.close();

    const wsInstance = new WebSocket(fullUrl);
    wsRef.current = wsInstance;
    setReadyState(wsInstance.readyState);

    wsInstance.onopen = () => {
      setReadyState(WebSocket.OPEN);
      setConnected(true);
      onOpen?.();
      console.log("✅ WebSocket connected");
    };

    wsInstance.onmessage = (event) => {
      try {
        const parsed = JSON.parse(event.data);
        onMessage?.(parsed);
      } catch (err) {
        console.warn("⚠️ Failed to parse WebSocket message:", event.data);
      }
    };

    wsInstance.onerror = (err) => {
      // Only report error if socket was connected
      if (wsRef.current?.readyState !== WebSocket.CLOSED) {
        console.error("⚠️ WebSocket error:", err);
        onError?.(err);
      }
    };

    wsInstance.onclose = () => {
      setReadyState(WebSocket.CLOSED);
      setConnected(false);
      onClose?.();
      console.log("🔌 WebSocket disconnected");

      if (reconnect && wsRef.current) {
        reconnectTimer.current = setTimeout(connect, reconnectInterval);
      }
    };
  }, [url, onMessage, onOpen, onClose, onError, reconnect, reconnectInterval]);

  const disconnect = useCallback(() => {
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setReadyState(WebSocket.CLOSED);
    setConnected(false);
  }, []);

  const sendMessage = useCallback((msg) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
    } else {
      console.warn("⚠️ WebSocket not connected");
    }
  }, []);

  // Cleanup when hook unmounts
  useEffect(() => () => disconnect(), [disconnect]);

  return { ws: wsRef.current, readyState, connected, sendMessage, connect, disconnect };
};
